import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import type { StatusLog } from 'config/api/actionInstanceStep/actionInstanceStep.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type StepStatusLogsProps = {
  stepId: number;
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const StepStatusLogs: React.FC<StepStatusLogsProps> = ({ stepId }) => {
  const { t } = useTranslation();

  const {
    data: logs,
    isLoading,
    isError,
  } = useQuery(['ACTION_INSTANCE_STEP_STATUS_LOGS', stepId], () =>
    actionInstanceStepApi.getStatusLogs(stepId, { limit: 500 }).then(res => res.data.results),
  );

  if (isError) {
    return (
      <Typography color='textSecondary' variant='body2'>
        {t(action_library_messages.errors.something_went_wrong_try_later)}
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Typography color='textSecondary' variant='body2'>
        {t(general_messages.loading)}
      </Typography>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Typography color='textSecondary' variant='body2'>
        {t(action_library_messages.instance.detail.action_log_empty)}
      </Typography>
    );
  }

  const sortedLogs = [...logs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) as StatusLog[];

  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>{t(action_library_messages.instance.detail.action_log_date)}</TableCell>
          <TableCell>{t(action_library_messages.instance.detail.action_log_user)}</TableCell>
          <TableCell>{t(action_library_messages.instance.detail.action_log_change)}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedLogs.map(log => (
          <TableRow key={`${log.id}-${log.createdAt}`}>
            <TableCell>{formatDate(log.createdAt)}</TableCell>
            <TableCell>{log.userFullName || log.userEmail || '-'}</TableCell>
            <TableCell>
              {log.oldStatusDisplay || '-'} → {log.newStatusDisplay}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default StepStatusLogs;
